// thumbnailPreloader.js - Blob URL caching for instant thumbnail display
import { API_BASE_URL } from '../config/runtime';

// Blob URL cache: original URL -> blob URL
const blobCache = new Map();
const loadingPromises = new Map();

// Track which URLs have been attempted (success or failure)
const attemptedUrls = new Set();

// Direct GCS URL (public read via IAM, no signed URL needed)
export const STORAGE_BUCKET = 'magi-f7f13.firebasestorage.app';
export const storageUrl = (path) =>
  `https://storage.googleapis.com/${STORAGE_BUCKET}/${path}`;

/**
 * Extract thumbnail URL from various data formats
 * NOTE: Priority order MUST match VideoThumbnail.jsx to ensure cache hits
 */
export const extractThumbnailUrl = (item) => {
  if (!item) return null;

  // Priority 1: Direct Firebase Storage URL from storage path (no backend roundtrip)
  if (item.thumbnail_storage_path) {
    return storageUrl(item.thumbnail_storage_path);
  }

  // Priority 2: Direct thumbnail URL (pre-stored)
  if (item.thumbnail_url && !item.thumbnail_url.includes('default_thumbnails')) {
    return item.thumbnail_url;
  }

  // Priority 3: Fallback to backend API for videos without storage path
  if (item.job_id && item.video_type) {
    return `${API_BASE_URL}/api/media/thumbnail/${item.job_id}`;
  }

  // Priority 4: Course thumbnails via backend
  if ((item.has_thumbnail) && !item.video_type) {
    const courseId = item.job_id || item.id;
    return `${API_BASE_URL}/api/media/course-thumbnail/${courseId}`;
  }

  // Priority 5: Fallback for videos with only job_id
  if (item.job_id) {
    return `${API_BASE_URL}/api/media/thumbnail/${item.job_id}`;
  }

  return null;
};

/**
 * Get cached blob URL for an item (for components to use)
 * Returns the blob URL if cached, null otherwise
 */
export const getBlobUrl = (item) => {
  const url = extractThumbnailUrl(item);
  if (!url) return null;
  return blobCache.get(url) || null;
};

/**
 * Preload a single image as a blob URL
 */
const preloadImageAsBlob = async (url) => {
  // Return cached blob URL if available
  if (blobCache.has(url)) {
    return { url, blobUrl: blobCache.get(url), cached: true };
  }

  // Return existing promise if already loading
  if (loadingPromises.has(url)) {
    return loadingPromises.get(url);
  }

  // Skip if already attempted and failed
  if (attemptedUrls.has(url) && !blobCache.has(url)) {
    return { url, blobUrl: null, failed: true };
  }

  // Create loading promise
  const loadPromise = (async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      const response = await fetch(url, {
        signal: controller.signal
        // No credentials needed - signed URLs contain auth in the URL itself
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Cache the blob URL
      blobCache.set(url, blobUrl);
      attemptedUrls.add(url);
      loadingPromises.delete(url);

      return { url, blobUrl, cached: false };
    } catch (error) {
      attemptedUrls.add(url);
      loadingPromises.delete(url);
      throw error;
    }
  })();

  loadingPromises.set(url, loadPromise);
  return loadPromise;
};

/**
 * Preload multiple thumbnails as blob URLs with concurrency control
 */
export const preloadThumbnails = async (items, options = {}) => {
  const {
    onProgress,
    concurrency = 6,
    priority = 'normal',
    maxItems = null
  } = options;

  // Extract all thumbnail URLs that aren't cached yet
  let urlsToLoad = items
    .map(extractThumbnailUrl)
    .filter(url => url && !blobCache.has(url) && !attemptedUrls.has(url));

  // Apply max items limit if specified
  if (maxItems && urlsToLoad.length > maxItems) {
    urlsToLoad = urlsToLoad.slice(0, maxItems);
  }

  const alreadyCached = items.filter(item => {
    const url = extractThumbnailUrl(item);
    return url && blobCache.has(url);
  }).length;

  if (urlsToLoad.length === 0) {
    console.log(`✅ All ${alreadyCached} thumbnails already cached as blobs`);
    return { loaded: 0, failed: 0, cached: alreadyCached };
  }

  // Adjust concurrency based on priority
  const actualConcurrency = priority === 'high' ? Math.min(12, concurrency * 2) :
                            priority === 'low' ? Math.max(2, Math.floor(concurrency / 2)) :
                            concurrency;

  console.log(`🖼️ Preloading ${urlsToLoad.length} thumbnails as blobs (priority: ${priority}, concurrency: ${actualConcurrency})`);

  let loaded = 0;
  let failed = 0;

  // Process in batches with concurrency control
  for (let i = 0; i < urlsToLoad.length; i += actualConcurrency) {
    const batch = urlsToLoad.slice(i, i + actualConcurrency);
    const batchPromises = batch.map(url =>
      preloadImageAsBlob(url)
        .then(result => {
          if (!result.cached && !result.failed) {
            loaded++;
          }
          if (onProgress) {
            onProgress({
              loaded,
              failed,
              total: urlsToLoad.length,
              progress: (loaded + failed) / urlsToLoad.length
            });
          }
          return result;
        })
        .catch(error => {
          failed++;
          // Don't spam console with warnings
          if (failed <= 3) {
            console.warn(`⚠️ Failed to preload thumbnail: ${error.message}`);
          }
          if (onProgress) {
            onProgress({
              loaded,
              failed,
              total: urlsToLoad.length,
              progress: (loaded + failed) / urlsToLoad.length
            });
          }
          return { url, error: error.message };
        })
    );

    await Promise.all(batchPromises);
  }

  console.log(`✅ Blob preload complete: ${loaded} loaded, ${failed} failed, ${alreadyCached} already cached`);

  return {
    loaded,
    failed,
    cached: alreadyCached
  };
};

/**
 * Preload thumbnails for a specific section with priority
 */
export const preloadSectionThumbnails = async (section, data) => {
  let items = [];
  let priority = 'normal';
  let maxItems = null;

  switch (section) {
    case 'lessons':
      items = data.lessons || [];
      priority = 'high';
      maxItems = 30;
      break;
    case 'courses':
      items = data.courses || [];
      priority = 'high';
      maxItems = 30;
      break;
    case 'solutions':
      items = data.solutions || [];
      priority = 'high';
      maxItems = 20;
      break;
    case 'recentVideos':
      items = data.recentVideos || [];
      priority = 'high';
      maxItems = 20;
      break;
    case 'explore':
      items = [
        ...(data.courses || []),
        ...(data.videos || []),
        ...(data.solutions || [])
      ];
      priority = 'medium';
      maxItems = 40;
      break;
    default:
      console.warn(`Unknown section: ${section}`);
      return { loaded: 0, failed: 0, cached: 0 };
  }

  return preloadThumbnails(items, { priority, maxItems });
};

/**
 * Preload visible thumbnails with high priority
 */
export const preloadVisibleThumbnails = async (items, options = {}) => {
  const { onProgress } = options;

  return preloadThumbnails(items, {
    priority: 'high',
    concurrency: 10,
    onProgress
  });
};

/**
 * Check if a thumbnail is already preloaded as blob
 */
export const isThumbnailPreloaded = (item) => {
  const url = extractThumbnailUrl(item);
  return url ? blobCache.has(url) : false;
};

/**
 * Clear the blob cache (useful for memory management)
 */
export const clearPreloadCache = () => {
  // Revoke all blob URLs to free memory
  for (const blobUrl of blobCache.values()) {
    URL.revokeObjectURL(blobUrl);
  }
  blobCache.clear();
  loadingPromises.clear();
  attemptedUrls.clear();
  console.log('🗑️ Thumbnail blob cache cleared');
};

/**
 * Get cache statistics
 */
export const getPreloadStats = () => {
  return {
    cached: blobCache.size,
    loading: loadingPromises.size,
    attempted: attemptedUrls.size,
    // Rough estimate: 100KB per thumbnail blob
    memoryEstimate: blobCache.size * 100 * 1024
  };
};

// Auto-clear cache if memory usage is too high (100MB limit for blobs)
if (typeof window !== 'undefined') {
  setInterval(() => {
    const stats = getPreloadStats();
    if (stats.memoryEstimate > 100 * 1024 * 1024) {
      console.warn('⚠️ Thumbnail blob cache memory limit exceeded, clearing...');
      clearPreloadCache();
    }
  }, 60000);
}

export default {
  preloadThumbnails,
  preloadSectionThumbnails,
  preloadVisibleThumbnails,
  isThumbnailPreloaded,
  getBlobUrl,
  extractThumbnailUrl,
  clearPreloadCache,
  getPreloadStats
};
